import { User } from 'firebase/auth';

import {
  EvaluationName,
  IntroTypes,
  Partner,
  CONSENT_SELECTION_ACC,
  CONSENT_SELECTION,
} from '@types';
import { stripQueryParams } from '@utils';

import { rudderstackService } from '../Rudderstack';

export class GTMService {
  private static _instance: GTMService;

  ga_client_id: string = '';

  private constructor() {}

  static getInstance() {
    if (!GTMService._instance) {
      GTMService._instance = new GTMService();
    }
    return GTMService._instance;
  }

  private sendGtmEvent = (data: object) => {
    if (localStorage.getItem(CONSENT_SELECTION) === CONSENT_SELECTION_ACC) {
      return window.dataLayer.push({
        ...data,
        ga_client_id: this.ga_client_id,
      });
    }
    return window.dataLayer.push(data);
  };

  sendGTMDataLayerContactInfoReceived = (partner: Partner) => {
    this.sendGtmEvent({
      event: 'contact_info_received',
      partner_id: partner.id,
      partner_ref_id: partner.reference_id,
      partner_name: partner.displayname,
    });
    rudderstackService.piiSubmitted();
  };

  sendGTMDataLayerDigitalOptInAccepted = (
    title: string,
    partnerName: string,
    program: EvaluationName
  ) => {
    this.sendGtmEvent({
      event: 'opted_for_digital_enrollment',
      pageTitle: title,
      affiliate_name: partnerName,
      program_type: program,
    });
    rudderstackService.digitalEnrollmentOptedIn(program);
  };

  sendGTMDataLayerOfferDigitalOptIn = (title: string, partnerName: string) => {
    this.sendGtmEvent({
      event: 'offer_digital_enrollment',
      pageTitle: title,
      affiliate_name: partnerName,
      page: {
        pageTitle: title,
      },
    });
    rudderstackService.digitalEnrollmentOffered();
  };

  sendVirtualPageview = (pageTitle: string) => {
    this.sendGtmEvent({
      event: 'virtualPageview',
      page: { pageTitle },
      pageTitle,
    });
  };

  private clientIdRequest?: Promise<string>;
  private resolveClientId?: (clientId: string) => void;

  setHostClientId = (clientId: string) => {
    this.ga_client_id = clientId;
    this.resolveClientId?.(clientId);
  };

  getGAClientId = (): Promise<string> => {
    if (this.ga_client_id) return Promise.resolve(this.ga_client_id);
    if (this.clientIdRequest) return this.clientIdRequest;
    this.clientIdRequest = new Promise<string>((resolve) => {
      let settled = false;
      const finish = (clientId: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.resolveClientId = undefined;
        resolve(clientId);
      };
      const timer = setTimeout(() => finish(this.ga_client_id), 1500);
      this.resolveClientId = finish;
      try {
        window.gtag('get', 'G-MHV2RMN', 'client_id', (clientId: string) => {
          // A delayed widget callback must never replace a host ID delivered meanwhile.
          if (typeof clientId === 'string' && clientId && !this.ga_client_id) {
            this.ga_client_id = clientId;
          }
          finish(this.ga_client_id);
        });
      } catch (error) {
        console.error('Error fetching GA client ID:', error);
        finish(this.ga_client_id);
      }
    }).finally(() => {
      this.clientIdRequest = undefined;
    });
    return this.clientIdRequest;
  };

  sendResultPageview = (pageTitle: string) => {
    this.sendGtmEvent({
      event: 'results_page_view',
      page: { pageTitle },
      pageTitle,
    });
  };

  initializeDataLayer = async (
    user: User,
    partner: Partner,
    language: string,
    variant: string,
    requestingHostLong?: string,
    gaClientId?: string,
    consentCookie?: string,
    introUI?: IntroTypes
  ) => {
    const data = {
      event: 'initialize',
      partner_id: partner.id!,
      partner_ref_id: 'TBD',
      affiliate_ref_id: 'TBD',
      partner_name: partner.displayname!,
      initialization_language: language,
      SDKreferrer: requestingHostLong
        ? stripQueryParams(requestingHostLong)
        : '',
      workflow_variant: variant,
      introUI: introUI || 'none',
    };
    let userPII = {};
    if (gaClientId) {
      this.setHostClientId(gaClientId);
    }

    if (consentCookie === CONSENT_SELECTION_ACC) {
      userPII = {
        engagement_id: user.uid,
        ga_client_id: gaClientId || (await this.getGAClientId()),
      };
    }
    window.dataLayer.push({ ...data, ...userPII });
    rudderstackService.appInitialized(language, introUI);
  };
}

export const gtm = GTMService.getInstance();
